'use client';

import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Popper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  MenuItem as MuiMenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import React, { useCallback, useContext, useRef, useState } from 'react';

import { AiStarSmall } from '@/components/assistant-ui/assets/aiStarSmall';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { ThemeContext } from '@/components/theme';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { CloudProviders } from '@/data/inventory/data';

export const ASSISTANT_MARGIN_BOTTOM = 24;
export const ASSISTANT_HEIGHT = 50;

export const AssistantMenuItem = ({
  href,
  title,
  selected,
}: {
  title: string;
  href: string;
  selected?: boolean;
}) => {
  return (
    <Stack
      height={`${ASSISTANT_HEIGHT}px`}
      className={`mb-[${ASSISTANT_MARGIN_BOTTOM}px]`}
    >
      <Box
        sx={{
          background: 'var(--mui-palette-custom-GRADIENT-AI)',
          height: '1px',
          width: '100%',
        }}
      />
      <Link
        href={href}
        className={classNames('slide-left h-[48px] min-h-[48px]', {
          'bg-white': selected,
          'text-[var(--mui-palette-custom-BLACK-01)]': selected,
          'hover:bg-[var(--mui-palette-primary-hover)]': !selected,
        })}
      >
        <Stack direction='row' alignItems='center' className='nav-item-icon'>
          <Box
            className='w-[20px] h-[20px]'
            sx={{ flexShrink: 0, '& svg': { width: '20px', height: '20px' } }}
          >
            <AiStarSmall />
          </Box>
          <Typography
            className={classNames(
              'slide-left collapse absolute left-[60px] w-[200px] opacity-0',
              {
                'text-[var(--mui-palette-custom-BLACK-01)]': selected,
                'text-white': !selected,
              }
            )}
          >
            {title}
          </Typography>
        </Stack>
      </Link>
      <Box
        sx={{
          background: 'var(--mui-palette-custom-GRADIENT-AI)',
          height: '1px',
          width: '100%',
        }}
      />
    </Stack>
  );
};

export const NavigationMenuItem = ({
  href,
  title,
  iconClass,
  testId,
  disabled,
  onClick,
  selected,
  variant = 'sidebar',
}: {
  title: string;
  href: string;
  iconClass: string;
  testId?: string;
  disabled?: boolean;
  onClick?: () => void;
  selected?: boolean;
  variant?: 'sidebar' | 'dropdown';
}) => {
  const isDropdown = variant === 'dropdown';

  return (
    <Link
      href={href}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={classNames('h-[48px] min-h-[48px]', {
        'slide-left': !isDropdown,
        'flex items-center px-[16px]': isDropdown,
        'bg-white': selected,
        'text-[var(--mui-palette-custom-BLACK-01)]': selected,
        'hover:bg-[var(--mui-palette-primary-hover)]': !selected && !disabled,
        'cursor-default': disabled,
      })}
    >
      <i
        className={classNames(`${iconClass} w-[20px] h-[20px]`, {
          'nav-item-icon': !isDropdown,
          'mr-[12px]': isDropdown,
          'text-[var(--mui-palette-custom-BLACK-01)]': isDropdown && selected,
          'text-white': isDropdown && !selected && !disabled,
          'text-[var(--mui-palette-custom-GREY-01)]': disabled,
        })}
      />
      <Typography
        className={classNames({
          'slide-left collapse absolute left-[60px] w-[200px] opacity-0':
            !isDropdown,
          'text-[var(--mui-palette-custom-BLACK-01)]': selected,
          'text-white': !selected && !disabled,
          'text-[var(--mui-palette-custom-GREY-01)]': disabled,
        })}
      >
        {title}
      </Typography>
    </Link>
  );
};

const navigationThemeToggleSx = {
  padding: '4px',
  '& i': {
    color: 'var(--mui-palette-primary-main)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: 'var(--mui-palette-background-primary)',
  },
};

export const NavigationActions = ({
  demoSettingsMenuOpen,
  onDemoSettingsMenuOpenChange,
}: {
  demoSettingsMenuOpen: boolean;
  onDemoSettingsMenuOpenChange: (open: boolean) => void;
}) => {
  const { theme, changeTheme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';
  const { isDemo, isDev } = useEnvironment();

  return (
    <Stack
      direction='row'
      className='navigation-actions slide-left collapse opacity-0'
      alignItems='center'
      gap='24px'
    >
      {(isDemo || isDev) && (
        <DemoSettingsMenu
          open={demoSettingsMenuOpen}
          onOpenChange={onDemoSettingsMenuOpenChange}
        />
      )}
      <ToggleButtonGroup
        exclusive
        value={isDarkMode ? 'dark' : 'light'}
        onChange={(_event, value) => {
          if (value === 'light' || value === 'dark') {
            changeTheme(value);
          }
        }}
        sx={{
          boxShadow: 'none',
          height: 'auto',
          border: 'none',
        }}
      >
        <Tooltip title='Light mode'>
          <ToggleButton
            value='light'
            data-testid='light-mode'
            aria-label='Light mode'
            sx={navigationThemeToggleSx}
          >
            <i className='material-symbols-light-mode-outline-rounded w-[20px] h-[20px]' />
          </ToggleButton>
        </Tooltip>
        <Tooltip title='Dark mode'>
          <ToggleButton
            value='dark'
            data-testid='dark-mode'
            aria-label='Dark mode'
            sx={navigationThemeToggleSx}
          >
            <i className='material-symbols-bedtime-outline-rounded w-[20px] h-[20px]' />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Stack>
  );
};

export interface OverflowMenuItem {
  key: string;
  href: string;
  title: string;
  iconClass: string;
  disabled?: boolean;
  isAssistant?: boolean;
}

export const MoreMenuItem = ({
  items,
  open,
  onOpenChange,
}: {
  items: OverflowMenuItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => {
    if (open) {
      onOpenChange(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Box
          ref={setAnchorEl}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onOpenChange(!open);
          }}
          className='slide-left h-[48px] min-h-[48px] cursor-pointer flex items-center hover:bg-[var(--mui-palette-primary-hover)]'
          sx={{
            '& i': { color: 'white' },
          }}
        >
          <i className='material-symbols-more-horiz w-[20px] h-[20px] nav-item-icon' />
          <Typography className='slide-left collapse absolute left-[60px] w-[200px] opacity-0 text-white'>
            More
          </Typography>
        </Box>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement='right-start'
          transition
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps}>
              <Paper
                className='dark'
                sx={{
                  boxShadow: 'var(--mui-customShadows-lg)',
                  backgroundImage: 'none',
                  bgcolor: 'var(--mui-palette-background-primary)',
                  minWidth: '200px',
                  py: 1,
                }}
              >
                <Stack>
                  {items.map((item) =>
                    item.isAssistant ? (
                      <Link
                        key={item.key}
                        href={item.href}
                        onClick={handleClose}
                        className='h-[48px] flex items-center px-[16px] hover:bg-[var(--mui-palette-primary-hover)]'
                      >
                        <Stack
                          alignItems='center'
                          sx={{
                            width: '20px',
                            height: '20px',
                            marginRight: '12px',
                            '& svg': { width: '20px', height: '20px' },
                          }}
                        >
                          <AiStarSmall />
                        </Stack>
                        <Typography className='text-white'>
                          {item.title}
                        </Typography>
                      </Link>
                    ) : (
                      <NavigationMenuItem
                        key={item.key}
                        href={item.href}
                        iconClass={item.iconClass}
                        title={item.title}
                        disabled={item.disabled}
                        onClick={handleClose}
                        variant='dropdown'
                      />
                    )
                  )}
                </Stack>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

const DemoSettingsMenu = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const { demoSettings, updateDemoSettings } = useEnvironment();
  const featureFlags = useFeatureFlags();
  const { assistantEnabled } = featureFlags;

  const handleSettingsClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!open) return;
      const target = event.target as Node;
      if (paperRef.current?.contains(target)) return;
      onOpenChange(false);
    },
    [open, onOpenChange]
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleSettingsClickAway}>
        <Box
          component='span'
          sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
        >
          <Tooltip title='Console Settings'>
            <Icon
              ref={setAnchorEl}
              onClick={(event) => {
                event.stopPropagation();
                onOpenChange(!open);
              }}
              iconClass='material-symbols-settings-outline-rounded'
              sx={{
                cursor: 'pointer',
                backgroundColor: 'var(--mui-palette-background-primary)',

                '&:hover': {
                  backgroundColor: 'var(--mui-palette-primary-hover)',
                },

                '& i': {
                  color: 'white',
                },
              }}
            />
          </Tooltip>
        </Box>
      </ClickAwayListener>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement='right'
        transition
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [12, 0],
            },
          },
        ]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              ref={paperRef}
              className='dark'
              sx={{
                boxShadow: 'var(--mui-customShadows-lg)',
                backgroundImage: 'none',
                bgcolor: 'var(--mui-palette-background-paper)',
                color: 'var(--mui-palette-text-primary)',
              }}
            >
              <Stack
                onClick={(event) => event.stopPropagation()}
                sx={{
                  width: '250px',
                  padding: '24px',
                }}
                gap='24px'
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Typography variant='h3'>Console Settings</Typography>
                  <Icon
                    iconClass='material-symbols-close-rounded'
                    onClick={() => {
                      onOpenChange(false);
                    }}
                  />
                </Stack>
                <Stack gap='24px'>
                  <FormControl>
                    <FormLabel>Primary Cloud</FormLabel>
                    <Select
                      value={demoSettings.primaryProvider}
                      onChange={(event) =>
                        updateDemoSettings({
                          ...demoSettings,
                          primaryProvider: event.target.value,
                        })
                      }
                      MenuProps={{
                        disablePortal: true,
                        slotProps: {
                          paper: { className: 'dark' },
                        },
                      }}
                    >
                      {(
                        [Provider.Aws, Provider.Azure, Provider.Gcp] as const
                      ).map((provider) => (
                        <MuiMenuItem key={provider} value={provider}>
                          {CloudProviders[provider].displayName}
                        </MuiMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Clouds</FormLabel>
                    {(
                      [
                        Provider.Aws,
                        Provider.Azure,
                        Provider.Gcp,
                        Provider.MongoAtlas,
                      ] as const
                    ).map((provider) => (
                      <FormControlLabel
                        className='my-[6px]'
                        key={provider}
                        control={<Switch className='mr-[12px]' />}
                        label={CloudProviders[provider].displayName}
                        checked={demoSettings[provider]}
                        onChange={(event, checked) => {
                          const updatedSettings = {
                            ...demoSettings,
                            [provider]: checked,
                          };

                          if (
                            !updatedSettings[updatedSettings.primaryProvider]
                          ) {
                            updatedSettings.primaryProvider =
                              (
                                [
                                  Provider.Aws,
                                  Provider.Azure,
                                  Provider.Gcp,
                                ] as const
                              ).find((x) => updatedSettings[x]) || Provider.Aws;
                          }

                          updateDemoSettings(updatedSettings);
                        }}
                      />
                    ))}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Features</FormLabel>
                    {!assistantEnabled && (
                      <FormControlLabel
                        className='my-[6px]'
                        control={<Switch className='mr-[12px]' />}
                        label='AI'
                        checked={demoSettings.ai}
                        onChange={(event, checked) =>
                          updateDemoSettings({
                            ...demoSettings,
                            ai: checked,
                          })
                        }
                      />
                    )}
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Log Space'
                      checked={demoSettings.logSpace}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          logSpace: checked,
                        })
                      }
                    />
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Ransomware'
                      checked={demoSettings.ransomware}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          ransomware: checked,
                        })
                      }
                    />
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Lakehouse'
                      checked={demoSettings.lakehouse}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          lakehouse: checked,
                        })
                      }
                    />
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Google Workspace'
                      checked={demoSettings.googleWorkspace}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          googleWorkspace: checked,
                        })
                      }
                    />
                  </FormControl>
                  <Button
                    className='mt-[12px]'
                    variant='outlined'
                    onClick={() => window.location.reload()}
                  >
                    Reload
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
